import { useEffect, useRef } from 'preact/hooks';
import type { StylePreset } from '../../types';
import { getAllRenderers } from '../../engine/styles/base';

interface Props {
  value: StylePreset;
  onChange: (preset: StylePreset) => void;
}

const STYLE_INFO: Record<string, { emoji: string }> = {
  simple: { emoji: '✏️' },
  bold: { emoji: '💪' },
  purupuru: { emoji: '💧' },
  glitter: { emoji: '✨' },
};

function ThumbnailCanvas({ preset, isActive }: { preset: string; isActive: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderers = getAllRenderers();
    const renderer = renderers.find((r) => r.name === preset);
    if (renderer) {
      const size = 60;
      canvas.width = size;
      canvas.height = size;
      renderer.renderThumbnail(ctx, size);
    }
  }, [preset]);

  return (
    <canvas
      ref={canvasRef}
      width={60}
      height={60}
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        border: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
      }}
    />
  );
}

export function StylePicker({ value, onChange }: Props) {
  const renderers = getAllRenderers();

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {renderers.map((r) => {
        const info = STYLE_INFO[r.name] ?? { emoji: '🎨' };
        const isActive = value === r.name;
        return (
          <button
            key={r.name}
            onClick={() => onChange(r.name as StylePreset)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '8px',
              border: isActive ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
              borderRadius: '12px',
              background: isActive ? 'var(--color-surface)' : 'white',
              cursor: 'pointer',
              fontSize: '0.8rem',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <ThumbnailCanvas preset={r.name} isActive={isActive} />
            <span style={{ fontSize: '0.75rem', fontWeight: isActive ? 'bold' : 'normal' }}>
              {info.emoji} {r.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
